import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authApi } from '../../services/authApi';
import { baseApi } from '../../services/baseApi';
import {
  User,
  LoginRequest,
  RegisterRequest,
  RegisterWithOrganizationRequest,
  AuthResponse,
  GoogleLoginRequest,
  GoogleRegisterWithOrganizationRequest,
  GoogleProfile,
  AUTH_ERROR_CODES,
} from '../../types/auth';

/**
 * Fallo de los flujos de Google. Es un objeto y no un string porque la pantalla de login
 * tiene que distinguir "este correo no tiene cuenta" (mandar a registrarse, con el perfil
 * ya cargado) de un error de verdad, y esa decisión no puede depender del texto.
 */
export interface GoogleAuthError {
  message: string;
  code?: string;
  profile?: GoogleProfile | null;
}

/** Traduce el error de axios de los endpoints de Google al objeto de arriba. */
const toGoogleAuthError = (error: any, fallback: string): GoogleAuthError => ({
  message: error?.response?.data?.message || error?.message || fallback,
  code: error?.response?.data?.code,
  profile: error?.response?.data?.profile ?? null,
});

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  // Trial info
  trialEndDate: string | null;
  isTrialExpired: boolean;
  maxUsers: number;
  plan: string | null;
}

const initialState: AuthState = {
  user: null,
  accessToken: localStorage.getItem('accessToken'),
  refreshToken: localStorage.getItem('refreshToken'),
  isAuthenticated: !!localStorage.getItem('accessToken'), // Set to true if token exists
  loading: false,
  error: null,
  // Trial info from localStorage
  trialEndDate: localStorage.getItem('trialEndDate'),
  isTrialExpired: localStorage.getItem('isTrialExpired') === 'true',
  maxUsers: parseInt(localStorage.getItem('maxUsers') || '1'),
  plan: localStorage.getItem('plan'),
};

/**
 * Sesión iniciada: mismo estado y mismo localStorage sin importar por dónde entró la
 * persona (contraseña, Google, alta nueva). Estaba copiado tres veces y cada copia era una
 * oportunidad de olvidar una llave —la sesión se rearma desde localStorage al recargar, así
 * que una llave faltante se ve como "se me cerró sola la sesión al refrescar".
 */
const applyAuthSuccess = (state: AuthState, payload: AuthResponse, context: string) => {
  const { accessToken, refreshToken, user, trialEndDate, isTrialExpired, maxUsers, plan } = payload;

  state.loading = false;
  state.user = user;
  state.accessToken = accessToken;
  state.refreshToken = refreshToken;
  state.isAuthenticated = true;
  state.error = null;
  state.trialEndDate = trialEndDate || null;
  state.isTrialExpired = isTrialExpired || false;
  state.maxUsers = maxUsers || 1;
  state.plan = plan || null;

  try {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('restaurant_persistent_session', 'true');
    if (trialEndDate) localStorage.setItem('trialEndDate', trialEndDate);
    localStorage.setItem('isTrialExpired', String(isTrialExpired || false));
    localStorage.setItem('maxUsers', String(maxUsers || 1));
    if (plan) localStorage.setItem('plan', plan);
  } catch (error) {
    console.error(`❌ CRITICAL ERROR: Failed to save ${context} tokens to localStorage:`, error);
  }
};

const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginRequest, { rejectWithValue, dispatch }) => {
    try {
      // Reset API cache before login to ensure fresh data for new user
      dispatch(baseApi.util.resetApiState());
      const response = await authApi.login(credentials);
      return response;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error al iniciar sesión';
      console.error('Login error:', errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const register = createAsyncThunk(
  'auth/register',
  async (userData: RegisterRequest, { rejectWithValue, dispatch }) => {
    try {
      // Reset API cache before register to ensure fresh data for new user
      dispatch(baseApi.util.resetApiState());
      const response = await authApi.register(userData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Registration failed');
    }
  }
);

const registerWithOrganization = createAsyncThunk(
  'auth/registerWithOrganization',
  async (userData: RegisterWithOrganizationRequest, { rejectWithValue, dispatch }) => {
    try {
      // Reset API cache before register to ensure fresh data for new user
      dispatch(baseApi.util.resetApiState());
      const response = await authApi.registerWithOrganization(userData);
      return response;
    } catch (error: any) {
      console.error('Registration error caught:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);

      // Handle different error formats
      let errorMessage = 'Error en el registro';

      if (error.response?.data) {
        const data = error.response.data;
        // Handle { message: "..." } format
        if (typeof data.message === 'string') {
          errorMessage = data.message;
        }
        // Handle { errors: { field: ["error1", "error2"] } } format (validation errors)
        else if (data.errors) {
          const firstError = Object.values(data.errors).flat()[0];
          if (firstError) {
            errorMessage = String(firstError);
          }
        }
        // Handle string response
        else if (typeof data === 'string') {
          errorMessage = data;
        }
        // Handle { title: "..." } format (ProblemDetails)
        else if (typeof data.title === 'string') {
          errorMessage = data.title;
        }
      } else if (error.message) {
        // Network errors or other axios errors
        errorMessage = error.message;
      }

      console.error('Final error message:', errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const loginWithGoogle = createAsyncThunk(
  'auth/loginWithGoogle',
  async (request: GoogleLoginRequest, { rejectWithValue, dispatch }) => {
    try {
      dispatch(baseApi.util.resetApiState());
      return await authApi.googleLogin(request);
    } catch (error: any) {
      return rejectWithValue(toGoogleAuthError(error, 'Error al iniciar sesión con Google'));
    }
  }
);

const registerWithOrganizationGoogle = createAsyncThunk(
  'auth/registerWithOrganizationGoogle',
  async (request: GoogleRegisterWithOrganizationRequest, { rejectWithValue, dispatch }) => {
    try {
      dispatch(baseApi.util.resetApiState());
      return await authApi.googleRegisterWithOrganization(request);
    } catch (error: any) {
      return rejectWithValue(toGoogleAuthError(error, 'Error en el registro con Google'));
    }
  }
);

const logout = createAsyncThunk(
  'auth/logout',
  async (_, { dispatch }) => {
    // Logout is best-effort: client-side cleanup is the ground truth. If the
    // backend rejects the call (expired token, network down, etc.) we still
    // give the user a clean logout — never surface the error.
    try {
      await authApi.logout();
    } catch (error) {
      console.warn('[auth] logout endpoint failed; clearing client state anyway:', error);
    }
    // Reset all API cache so the next user starts fresh.
    dispatch(baseApi.util.resetApiState());
    return true;
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    /**
     * Refleja el avatar recién guardado sin re-loguear. El backend ya lo devuelve
     * en el login y en /auth/me, pero esos sólo corren al entrar: sin esto el chip
     * del header seguiría mostrando el icono viejo hasta la siguiente sesión.
     */
    setAvatarIcon: (state, action: PayloadAction<string | null>) => {
      if (state.user) {
        state.user = { ...state.user, avatarIcon: action.payload };
      }
    },
    setCredentials: (state, action: PayloadAction<AuthResponse>) => {
      const { accessToken, refreshToken, user } = action.payload;
      state.user = user;
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      state.isAuthenticated = true;
      state.error = null;

      // Store in localStorage for persistent restaurant sessions
      try {
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('restaurant_persistent_session', 'true');

        // Verify tokens were actually saved
        const savedAccessToken = localStorage.getItem('accessToken');
        const savedRefreshToken = localStorage.getItem('refreshToken');

        if (!savedAccessToken || !savedRefreshToken) {
          console.error('❌ WARNING: Tokens not found in localStorage after save!');
          console.error('📝 savedAccessToken:', !!savedAccessToken);
          console.error('📝 savedRefreshToken:', !!savedRefreshToken);
        }
      } catch (error) {
        console.error('❌ CRITICAL ERROR: Failed to save tokens to localStorage:', error);
        console.error('📝 This may indicate browser privacy settings or storage quota issues');
      }
    },
    clearCredentials: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;

      // Clear from localStorage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('restaurant_persistent_session');

    },
    updateSubscriptionInfo: (state, action: PayloadAction<{
      plan: string;
      isTrialExpired: boolean;
      maxUsers: number;
      trialEndDate?: string | null;
    }>) => {
      const { plan, isTrialExpired, maxUsers, trialEndDate } = action.payload;
      state.plan = plan;
      state.isTrialExpired = isTrialExpired;
      state.maxUsers = maxUsers;
      state.trialEndDate = trialEndDate || null;

      // Update localStorage
      try {
        localStorage.setItem('plan', plan);
        localStorage.setItem('isTrialExpired', String(isTrialExpired));
        localStorage.setItem('maxUsers', String(maxUsers));
        if (trialEndDate) {
          localStorage.setItem('trialEndDate', trialEndDate);
        } else {
          localStorage.removeItem('trialEndDate');
        }
      } catch (error) {
        console.error('❌ Error updating subscription info in localStorage:', error);
      }
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        applyAuthSuccess(state, action.payload, 'login');
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      });

    // Login con Google — misma sesión, distinta forma de comprobar quién eres
    builder
      .addCase(loginWithGoogle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        applyAuthSuccess(state, action.payload, 'google login');
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        const payload = action.payload as GoogleAuthError | undefined;
        // "Ese correo no tiene cuenta" no es un error que mostrar: el componente
        // navega al registro. Pintar la alerta roja aquí la dejaría colgada en la
        // pantalla siguiente.
        state.error =
          payload?.code === AUTH_ERROR_CODES.googleNoAccount
            ? null
            : payload?.message ?? 'Error al iniciar sesión con Google';
      });

    // Registro de organización con Google
    builder
      .addCase(registerWithOrganizationGoogle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerWithOrganizationGoogle.fulfilled, (state, action) => {
        applyAuthSuccess(state, action.payload, 'google org registration');
      })
      .addCase(registerWithOrganizationGoogle.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.error = (action.payload as GoogleAuthError | undefined)?.message ?? 'Error en el registro con Google';
      });

    // Register
    builder
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        const { accessToken, refreshToken, user } = action.payload;
        state.user = user;
        state.accessToken = accessToken;
        state.refreshToken = refreshToken;
        state.isAuthenticated = true;

        // Store in localStorage for persistent restaurant sessions
        try {
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', refreshToken);
          localStorage.setItem('restaurant_persistent_session', 'true');

          // Verify tokens were actually saved
          const savedAccessToken = localStorage.getItem('accessToken');
          const savedRefreshToken = localStorage.getItem('refreshToken');

          if (!savedAccessToken || !savedRefreshToken) {
            console.error('❌ WARNING: Tokens not found in localStorage after registration save!');
          }
        } catch (error) {
          console.error('❌ CRITICAL ERROR: Failed to save registration tokens to localStorage:', error);
        }
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      });

    // Register with Organization
    builder
      .addCase(registerWithOrganization.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerWithOrganization.fulfilled, (state, action) => {
        applyAuthSuccess(state, action.payload, 'org registration');
      })
      .addCase(registerWithOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      });

    // Logout
    builder
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        // The thunk is best-effort and never rejects, so this is the only
        // logout outcome. See logout thunk above for rationale.
        state.loading = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.error = null;
        // Clear trial info
        state.trialEndDate = null;
        state.isTrialExpired = false;
        state.maxUsers = 1;
        state.plan = null;

        // Clear from localStorage
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('restaurant_persistent_session');
        localStorage.removeItem('trialEndDate');
        localStorage.removeItem('isTrialExpired');
        localStorage.removeItem('maxUsers');
        localStorage.removeItem('plan');
      });
  },
});

export const { clearError, setCredentials, clearCredentials, updateSubscriptionInfo, setAvatarIcon } = authSlice.actions;
export { login, register, registerWithOrganization, loginWithGoogle, registerWithOrganizationGoogle, logout };
export default authSlice.reducer;

